/**
 * RuleComplianceOverview Component
 *
 * Main container component for rule compliance statistics dashboard
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Grid } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useRuleComplianceStats } from '../../hooks/useRuleComplianceStats';
import type { RulesAnalyticsFilters } from '../rules/RulesAnalyticsFilters';

import { ProviderComparisonTable } from './ProviderComparisonTable';
import { RuleComplianceStatCards } from './RuleComplianceStatCards';
import { RuleComplianceStatsTable } from './RuleComplianceStatsTable';

interface RuleComplianceOverviewProps {
  /** Optional leftmost card to display before stat cards */
  leftmostCard?: React.ReactNode;
  /** Filter parameters */
  filters?: RulesAnalyticsFilters;
  /** Organization ID for admin mode */
  organizationId?: string;
  /** Callback when edit button is clicked for a rule */
  onEditRule?: (ruleId: string) => void;
}

export const RuleComplianceOverview: React.FC<RuleComplianceOverviewProps> = ({
  leftmostCard,
  filters,
  organizationId,
  onEditRule,
}) => {
  const navigate = useNavigate();
  const { jotauditDashboard } = useEventTracking();
  
  const {
    rules,
    mostViolatedRule,
    mostCompliantRule,
    totalRules,
    overallComplianceRate,
    isLoading,
    error,
    refetch,
    byProvider,
  } = useRuleComplianceStats({
    organizationId,
    days: filters?.days,
    dateFrom: filters?.dateFrom,
    dateTo: filters?.dateTo,
    userIds: filters?.userIds?.join(','),
    ruleIds: filters?.ruleIds?.join(','),
    comparisonMode: filters?.comparisonMode,
  });

  // Handle rule click to navigate to analytics page
  const handleRuleClick = useCallback(
    (ruleId: string, ruleName?: string, complianceRate?: number, violationCount?: number) => {
      // Track rule click
      jotauditDashboard.ruleClicked({
        rule_id: ruleId,
        rule_name: ruleName,
        compliance_rate: complianceRate,
        violation_count: violationCount,
      });

      if (organizationId) {
        navigate(`/admin-v2/jotaudit/${organizationId}/rules/${ruleId}/analytics`);
      } else {
        navigate(`/jotaudit/rules/${ruleId}/analytics`);
      }
    },
    [navigate, organizationId, jotauditDashboard],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading rule compliance statistics</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Stat Cards Row (with optional leftmost card) */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {leftmostCard && (
          <Grid item xs={12} sm={6} md={3}>
            {leftmostCard}
          </Grid>
        )}
        <RuleComplianceStatCards
          mostViolatedRule={mostViolatedRule}
          mostCompliantRule={mostCompliantRule}
          totalRules={totalRules}
          overallComplianceRate={overallComplianceRate}
          isLoading={isLoading}
          hasLeftmostCard={!!leftmostCard}
          onRuleClick={handleRuleClick}
          organizationId={organizationId}
        />
      </Grid>

      {/* Comparison Table or Rules Table */}
      {filters?.comparisonMode && byProvider ? (
        <Box sx={{ mb: 3 }}>
          <ProviderComparisonTable byProvider={byProvider} rules={rules} isLoading={isLoading} />
        </Box>
      ) : (
        <RuleComplianceStatsTable
          rules={rules}
          isLoading={isLoading}
          onRuleClick={handleRuleClick}
          onEditRule={onEditRule}
          organizationId={organizationId}
        />
      )}
    </Box>
  );
};

export default RuleComplianceOverview;

